import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import DepartmentAllocationReport from "@/components/reports/DepartmentAllocationReport"
import DepartmentTrendlineChart from "@/components/analytics/DepartmentTrendlineChart"
import { useOvertime } from "@/contexts/overtime/useOvertime"
import DailyOvertimeDepartmentTable from "@/components/reports/DailyOvertimeDepartmentTable"
import OvertimeReasonsBarChart from "@/components/analytics/OvertimeReasonsBarChart"
import OvertimeReasonsLeaderboard from "@/components/analytics/OvertimeReasonsLeaderboard"
import { DatePicker } from 'antd';
import { OvertimeStatus } from "@/types/enums";
import 'antd/dist/reset.css';
import dayjs from 'dayjs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { LineChart, Line, CartesianGrid, XAxis } from 'recharts';
import { TrendingUp } from 'lucide-react';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { ExportButton } from "@/components/ui/ExportButton";
const { RangePicker } = DatePicker;

// Helper to get daily OT data in the required format
function getChartData(overtimeRequests, startDate, endDate) {
  if (!startDate || !endDate) return [];
  const dateMap = {};
  overtimeRequests.forEach(req => {
    if (!req.date || req.status !== OvertimeStatus.APPROVED) return;
    const dateStr = req.date;
    if (!dateMap[dateStr]) dateMap[dateStr] = 0;
    dateMap[dateStr] += Number(req.hours) || 0;
  });
  // Generate all dates in range
  const dates = [];
  const current = new Date(startDate);
  current.setHours(0,0,0,0);
  const last = new Date(endDate);
  last.setHours(0,0,0,0);
  while (current <= last) {
    const dateStr = current.toISOString().split('T')[0];
    dates.push({
      date: dateStr,
      hours: dateMap[dateStr] || 0,
    });
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

const chartConfig = {
  hours: {
    label: "Total OT Hours -",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

const DashboardReports = () => {
  const [activeReportTab, setActiveReportTab] = useState("daily-overtime")
  const { overtimeRequests, loading } = useOvertime();
  // Set default date range to last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 29); // Inclusive of today
  const [startDate, setStartDate] = useState<Date | undefined>(thirtyDaysAgo);
  const [endDate, setEndDate] = useState<Date | undefined>(today);

  return (
    <TooltipProvider delayDuration={0}>
      <div className="w-full overflow-x-auto">
        <div className="space-y-6">
          {/* Report Generator Section */}
          {/* Remove the ReportGenerator Card section entirely */}

          {/* Report Viewer Section */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between border-b px-3 sticky top-0 z-10 bg-white">
                <Tabs defaultValue="daily-overtime" value={activeReportTab} onValueChange={setActiveReportTab}>
                  <TabsList className="bg-transparent h-12">
                    <TabsTrigger value="daily-overtime" className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none">
                      Daily Overtime
                    </TabsTrigger>
                    <TabsTrigger value="department-allocation" className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none">
                      Department Allocation
                    </TabsTrigger>
                    <TabsTrigger value="overtime-reasons" className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none">
                      Overtime Reasons
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="ml-auto">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <RangePicker
                          value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                          onChange={(dates) => {
                            setStartDate(dates && dates[0] ? dates[0].toDate() : undefined);
                            setEndDate(dates && dates[1] ? dates[1].toDate() : undefined);
                          }}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Please reset the date before selecting a new start and end date.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <Tabs defaultValue="daily-overtime" value={activeReportTab} onValueChange={setActiveReportTab}>
                <div className="p-6">
                  <TabsContent value="daily-overtime" className="m-0">
                    <div className="mt-0">
                      <Card className="shadow-md hover:shadow-lg transition-shadow duration-200">
                        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-4 px-6">
                          <CardTitle className="text-xl font-bold text-left">
                            Daily Total Overtime Hours
                          </CardTitle>
                          <div className="flex items-center gap-2 ml-auto">
                            <ExportButton
                              onClick={() => {
                                // Export chart data as CSV
                                const chartData = getChartData(overtimeRequests || [], startDate, endDate);
                                const headers = ['Date', 'Hours'];
                                const csvContent = [
                                  headers.join(','),
                                  ...chartData.map(item => [item.date, item.hours].join(','))
                                ].join('\n');
                                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.setAttribute('href', url);
                                link.setAttribute('download', `daily_total_overtime_hours_${startDate ? dayjs(startDate).format('YYYYMMDD') : ''}_${endDate ? dayjs(endDate).format('YYYYMMDD') : ''}.csv`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              disabled={loading || !overtimeRequests?.length}
                            />
                          </div>
                        </CardHeader>
                        <CardContent>
                          <CardDescription>{startDate && endDate ? `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(endDate).format('MMM D, YYYY')}` : ''}</CardDescription>
                          <ChartContainer config={chartConfig}>
                            <LineChart
                              accessibilityLayer
                              data={getChartData(overtimeRequests || [], startDate, endDate)}
                              margin={{ left: 12, right: 12 }}
                            >
                              <CartesianGrid vertical={false} />
                              <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={(value) => dayjs(value).format('MMM D')}
                              />
                              <ChartTooltip
                                cursor={false}
                                content={<ChartTooltipContent hideLabel />}
                              />
                              <Line
                                dataKey="hours"
                                type="natural"
                                stroke="#38BDF8"
                                strokeWidth={2}
                                dot={{ fill: "#38BDF8" }}
                                activeDot={{ r: 6 }}
                              />
                            </LineChart>
                          </ChartContainer>
                        </CardContent>
                        <CardFooter className="flex-col items-start gap-2 text-sm">
                          <div className="flex gap-2 leading-none font-medium">
                            Daily overtime hours trend <TrendingUp className="h-4 w-4" />
                          </div>
                          <div className="text-muted-foreground leading-none">
                            Showing total approved overtime hours per day
                          </div>
                        </CardFooter>
                      </Card>
                    </div>
                    <div className="mt-8">
                      <DepartmentTrendlineChart
                        requests={overtimeRequests || []}
                        title="Department Overtime Distribution"
                        isLoading={loading}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </div>
                    <div className="mt-8">
                      <DailyOvertimeDepartmentTable
                        overtimeRequests={overtimeRequests || []}
                        startDate={startDate}
                        endDate={endDate}
                        isLoading={loading}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="department-allocation" className="m-0">
                    <DepartmentAllocationReport
                      startDate={startDate}
                      endDate={endDate}
                    />
                  </TabsContent>
                  <TabsContent value="overtime-reasons" className="m-0">
                    <div className="space-y-6">
                      {/* Overtime Reasons Analytics Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <OvertimeReasonsBarChart 
                          startDate={startDate?.toISOString().split('T')[0]}
                          endDate={endDate?.toISOString().split('T')[0]}
                        />
                        <OvertimeReasonsLeaderboard 
                          startDate={startDate?.toISOString().split('T')[0]}
                          endDate={endDate?.toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  )
}

export default DashboardReports